import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { OrderItemRatingFieldsFragment } from '@/types';
import { Text, View } from 'react-native';

export interface OrderItemType {
  metadata: {
    description: string | null;
    isFee?: boolean;
    notes?: string | null;
    orderItemRating: OrderItemRatingFieldsFragment | null;
    quantity: number;
    totalPrice: string;
  };
  subtitle?: React.ReactNode;
  title: string;
  value: string;
}

const TITLE_MAX_LINES = 2;
const DESCRIPTION_MAX_LINES = 4;

interface Props {
  children: React.ReactNode;
  description: string | null;
  formattedDiscount?: string | null;
  formattedPrice: string | null;
  hoverTargetRef?: React.Ref<View>;
  isFee?: boolean;
  loyaltyPointsLabel?: string | null;
  name: string;
  onRemove?: () => Promise<void>;
  rightContent?: React.ReactNode;
}

export const OrderItemTopRow: React.FC<Props> = ({
  children,
  description,
  formattedDiscount,
  formattedPrice,
  hoverTargetRef,
  isFee,
  loyaltyPointsLabel,
  name,
  rightContent,
}) => {
  const [styles] = useStyles();

  const savingLabel = formattedDiscount
    ? i18n.t('saving_amount', { amount: formattedDiscount })
    : null;
  const priceSubline =
    [savingLabel, loyaltyPointsLabel].filter(Boolean).join(' • ') || null;

  const renderDefaultRightContent = formattedPrice ? (
    <View style={styles.priceContainer}>
      <Text style={[TYPOGRAPHY.medium]}>{formattedPrice}</Text>
      {priceSubline ? (
        <Text
          style={[TYPOGRAPHY.xsmall, styles.savingText]}
          testID="order-item-price-subline"
        >
          {priceSubline}
        </Text>
      ) : null}
    </View>
  ) : null;

  return (
    <View ref={hoverTargetRef} style={styles.base}>
      <View style={[styles.content, isFee ? styles.contentCenter : null]}>
        <View style={styles.leftContainer}>
          <Text
            numberOfLines={TITLE_MAX_LINES}
            style={[TYPOGRAPHY.medium, FONTS.primarySemiBold]}
          >
            {name}
          </Text>
          {description ? (
            <Text
              numberOfLines={DESCRIPTION_MAX_LINES}
              style={[TYPOGRAPHY.description, styles.description]}
            >
              {description}
            </Text>
          ) : null}
        </View>
        <View style={styles.rightContainer}>
          {rightContent ?? renderDefaultRightContent}
        </View>
      </View>
      {children}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  contentCenter: {
    justifyContent: 'center',
    marginBottom: 0,
  },
  description: {
    lineHeight: 16,
    marginVertical: 4,
  },
  leftContainer: {
    flex: 1,
  },
  priceContainer: {
    alignItems: 'flex-end',
  },
  rightContainer: {
    flexDirection: 'row',
  },
  savingText: {
    color: theme.sk.color.text.success,
    marginVertical: 4,
  },
}));
